import CheckIcon from "@mui/icons-material/Check";
import {
  FormControl,
  Grid,
  List,
  ListItemButton,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useLanguageSkills from "../../../../hooks/useLanguageSkills";
import LanguageSkillLevels from "./LanguageSkillLevels";

type Props = {
  disabledLanguageCodes?: string[];
  disableLanguageSelect?: boolean;
};

const LanguagesForm = ({
  disabledLanguageCodes,
  disableLanguageSelect,
}: Props) => {
  const { control, watch } = useFormContext();
  const { t } = useTranslation();
  const theme = useTheme();

  const { sortedLanguageSkills } = useLanguageSkills();

  return (
    <>
      <Grid item container xs={3} justifyContent="flex-end" alignItems="center">
        <Typography textAlign="end" variant="secondary">
          {t("languageSkill:language")}
        </Typography>
      </Grid>
      <Grid item container xs={9} alignItems="center">
        <Controller
          name="languageCode"
          control={control}
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <Grid item container xs={9} alignItems="center">
              <FormControl fullWidth size="small">
                <Select
                  value={value}
                  onChange={onChange}
                  disabled={disableLanguageSelect}
                >
                  {sortedLanguageSkills.map((languageSkill) => (
                    <MenuItem
                      key={languageSkill.id}
                      value={languageSkill.code}
                      disabled={disabledLanguageCodes?.includes(
                        languageSkill.code,
                      )}
                    >
                      {languageSkill.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        />
      </Grid>
      <Grid item container xs={3} />
      <Grid item container xs={9} alignItems="center">
        <List sx={{ width: "100%", maxWidth: "430px" }}>
          <Controller
            name={"skillLevel"}
            control={control}
            rules={{ required: true }}
            render={({ field: { value, onChange }, formState }) => (
              <>
                {LanguageSkillLevels.map((level) => {
                  const selected = value === level;
                  const typography = {
                    fontSize: "14px",
                    color: selected ? theme.palette.primary.main : "#895FDFE5",
                    font: theme.typography.header.fontFamily,
                  };

                  return (
                    <ListItemButton
                      disabled={!watch("languageCode")}
                      onClick={() => onChange(level)}
                      key={"level-" + level}
                      sx={{
                        marginTop: "10px",
                        marginBottom: "10px",
                        border: selected ? "2px solid" : "1px solid",
                        borderRadius: "8px",
                        borderColor: selected
                          ? theme.palette.primary.main
                          : "#895FDFE5",
                      }}
                    >
                      <Grid container>
                        <Grid item xs={9} container>
                          <Typography
                            sx={{
                              ...typography,
                              fontWeight: 700,
                            }}
                          >
                            {t("languageSkill:" + level)}
                          </Typography>
                        </Grid>
                        <Grid item container xs={3} justifyContent="right">
                          {selected && <CheckIcon color="success" />}
                        </Grid>
                        <Grid item xs={12} container alignItems="center">
                          <Typography
                            sx={{
                              ...typography,
                              fontWeight: 400,
                            }}
                          >
                            {t("languageSkill:" + level + "_description")}
                          </Typography>
                        </Grid>
                      </Grid>
                    </ListItemButton>
                  );
                })}
              </>
            )}
          />
        </List>
      </Grid>
    </>
  );
};

export default LanguagesForm;
